import { AdvertiserPage, AdvertiserPageGroup } from 'services/AdvertiserPagesService';

// How a followed brand's daily fetch is doing, from its pages' last checks.
// Each page is fetched on its own (one Ad Library listing per page), so a brand
// is only as current as its least recently checked page.

/** The daily run is at 5am Pacific; past this, at least one morning was missed. */
export const STALE_AFTER_HOURS = 36;

export const FOLLOW_STATE = Object.freeze({
  OK: 'ok',
  WAITING: 'waiting', // followed, not fetched yet
  STALE: 'stale',
  ERROR: 'error',
} as const);
export type FollowState = typeof FOLLOW_STATE[keyof typeof FOLLOW_STATE];

export interface FollowedBrandStatus {
  state: FollowState;
  /** The least recent successful check across the brand's pages; null if any page was never checked. */
  checkedAt: string | null;
  /** New ads the pages' last checks added. */
  newAds: number;
  /** Pages whose latest outcome is a failure. */
  errors: Array<{ pageName: string; message: string }>;
  /** Active pages in the brand: what the daily run fetches. */
  pageCount: number;
}

// Snowflake returns 'YYYY-MM-DD HH:mm:ss.SSS' in UTC with no zone (checked against
// a real check: written at 22:30 UTC, read back as '22:30'), so it is read as UTC.
// A string that carries its own zone parses as is.
export function timeOf(value: string | null): number | null {
  if (!value) return null;
  const iso = /[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value.replace(' ', 'T')}Z`;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function followedBrandStatus(group: AdvertiserPageGroup, pages: AdvertiserPage[], now = Date.now()): FollowedBrandStatus {
  const members = pages.filter(p => p.isActive && group.pageIds.includes(p.pageId));
  // A failure counts until a later check succeeds.
  const errors = members
    .filter((p) => {
      const failed = timeOf(p.staticFollowErrorAt);
      const checked = timeOf(p.staticFollowCheckedAt);
      return !!p.staticFollowError && failed != null && (checked == null || failed > checked);
    })
    .map(p => ({ pageName: p.pageName, message: p.staticFollowError as string }));

  const times = members.map(p => timeOf(p.staticFollowCheckedAt));
  const neverChecked = !members.length || times.some(t => t == null);
  const oldest = neverChecked ? null : Math.min(...(times as number[]));
  const checkedAt = oldest == null ? null : members.find(p => timeOf(p.staticFollowCheckedAt) === oldest)!.staticFollowCheckedAt;
  const newAds = members.reduce((n, p) => n + (p.staticFollowNewAds || 0), 0);

  let state: FollowState = FOLLOW_STATE.OK;
  if (errors.length) state = FOLLOW_STATE.ERROR;
  else if (neverChecked) state = FOLLOW_STATE.WAITING;
  else if (now - (oldest as number) > STALE_AFTER_HOURS * 3600 * 1000) state = FOLLOW_STATE.STALE;

  return { state, checkedAt, newAds, errors, pageCount: members.length };
}
